import React, { Component } from 'react';
import moment, { Moment } from 'moment';
import { navigate } from '@reach/router';
import { Box } from 'bloomer/lib/elements/Box';
import { Title } from 'bloomer/lib/elements/Title';
import { Field } from 'bloomer/lib/elements/Form/Field/Field';
import { Label } from 'bloomer/lib/elements/Form/Label';
import Animal from '../Entities/Animal';
import { Select } from 'bloomer/lib/elements/Form/Select';
import { Control } from 'bloomer/lib/elements/Form/Control';
import { Input } from 'bloomer/lib/elements/Form/Input';
import { TextArea } from 'bloomer/lib/elements/Form/TextArea';
import { Button } from 'bloomer/lib/elements/Button';
import { confirmSuccessAfter, confirmPopupBefore } from '../util/helperFunctions';
import { AnimalOptionsConsumer } from '../DataProviders/AnimalOptions';
import Conditional from './Conditional';
import { Checkbox } from 'bloomer/lib/elements/Form/Checkbox';
import Spacer from '../elements/Spacer';
import SingleDatePicker from '../elements/SingleDatePicker';
import api from '../util/api';
import { Icon } from 'bloomer/lib/elements/Icon';
import styled from 'styled-components';
import AddLocationModal from './AddLocationModal';

interface PropTypes {
  animal: Animal;
  specialNeeds: SpecialNeed[];
  isNewAnimal?: boolean;
}

interface StateTypes {
  animal: Animal;
  focusState: boolean;
  locations: ILocation[];
  isModalOpen: boolean;
}

const FlexSelect = styled.div`
  display: flex;
  .button {
    height: auto;
  }
`;

class AnimalEditForm extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    animal: this.props.animal.clone(),
    focusState: false,
    locations: [],
    isModalOpen: false,
  };

  componentDidMount() {
    this.getLocations();
  }

  getLocations = async () => {
    const locations = await api.locations.get();
    this.setState({
      locations,
    });
  };

  parseEventValue = (target: HTMLInputElement) => {
    const eventId = target.id;
    let eventValue;
    // translate strings to litteral booleans
    if (target.value === 'true') {
      eventValue = true;
    } else if (target.value === 'false') {
      eventValue = false;
    } else if (target.value === 'undefined') {
      eventValue = undefined;
    } else if (target.value === 'null') {
      eventValue = null;
    } else if (eventId === 'aNumber') {
      eventValue = parseInt(target.value, 10);
    } else {
      eventValue = target.value;
    }
    return eventValue;
  };

  setAnimalState = (obj: Partial<Animal>) => {
    const animal = this.state.animal.clone();
    Object.assign(animal, obj);
    this.setState({ animal });
  };

  getNextAssessmentDue = (value: 'full' | 'initial' | string) => {
    let newAssessmentDueTimestamp: moment.Moment | null = moment();

    if (value === 'full') {
      newAssessmentDueTimestamp.add(48, 'hours');
    } else if (value === 'initial') {
      newAssessmentDueTimestamp.add(24, 'hours');
    } else {
      newAssessmentDueTimestamp = null;
    }

    return newAssessmentDueTimestamp ? newAssessmentDueTimestamp.toISOString() : newAssessmentDueTimestamp;
  };

  handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const value = this.parseEventValue(target);
    const update = {
      [target.id]: value,
    } as any;
    if (target.id === 'assessmentStatus') {
      update.nextAssessmentDue = this.getNextAssessmentDue(value as string);
    }

    this.setAnimalState(update);
  };

  handleLocationChange = (e: React.FormEvent<HTMLSelectElement>) => {
    const locationName = (e.target as HTMLSelectElement).value;
    if (locationName === 'unknown') {
      return this.setAnimalState({
        location: null,
      });
    }

    const { locations } = this.state;
    const newLocation = locations.find(location => location.name === locationName);
    if (newLocation) {
      return this.setAnimalState({
        location: newLocation,
      });
    }
  };

  handleSpecialNeedChange = (specialNeed: SpecialNeed, e: React.FormEvent<HTMLInputElement>) => {
    const { animal } = this.state;
    const target = e.target as HTMLInputElement;
    let newNeeds;
    if (target.checked) {
      newNeeds = [...animal.specialNeeds, specialNeed];
    } else {
      newNeeds = animal.specialNeeds.filter(need => need._id !== specialNeed._id);
    }

    this.setAnimalState({
      specialNeeds: newNeeds,
    });
  };

  handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { animal } = this.state;
    const executeSave = async () => {
      await confirmSuccessAfter(`${animal.name} successfully saved!`, async () => {
        await animal.save();
        navigate('/manageAnimals/animalList');
      });
    };

    if (animal.aNumber.toString().length < 8) {
      confirmPopupBefore(
        `A#${animal.aNumber} looks like it might be too short, are you sure this aNumber is correct?`,
        executeSave,
      );
    } else {
      executeSave();
    }
  };

  handleDateFocusChange = ({ focused }: { focused: boolean | null }) =>
    this.setState({
      focusState: Boolean(focused),
    });

  handleDateChange = (date: Moment | null) => {
    if (!date) return;
    this.setAnimalState({ arrivalDate: date.toISOString() });
  };

  toggleModal = () => {
    this.setState({
      isModalOpen: !this.state.isModalOpen,
    });
  };

  render() {
    const { animal, focusState, locations, isModalOpen } = this.state;
    const { specialNeeds, isNewAnimal } = this.props;
    return (
      <>
        <form onSubmit={this.handleSubmit}>
          <Box>
            <Title tag="h2" isSize={2} id="editAnimalFormHeading">
              Animal Information:
            </Title>
            <Field>
              <Conditional check={isNewAnimal === true} alt={<p className="is-size-3">A#{animal.aNumber}</p>}>
                <>
                  <Label htmlFor="aNumber">aNumber</Label>
                  <Control>
                    <Input
                      isSize="medium"
                      onChange={this.handleChange}
                      value={animal.aNumber === 0.0 ? '' : animal.aNumber}
                      id="aNumber"
                      type="number"
                      required={true}
                    />
                  </Control>
                </>
              </Conditional>
            </Field>
            <Field>
              <Label isSize="medium" htmlFor="animalType">
                Type:
              </Label>
              <Control>
                <Select
                  isSize="medium"
                  onChange={this.handleChange}
                  value={animal.animalType}
                  id="animalType"
                  required={true}
                >
                  <option value=""> - </option>
                  <option value="dog"> Dog </option>
                  <option value="cat"> Cat </option>
                </Select>
              </Control>
            </Field>
            <Field>
              <Label isSize="medium" htmlFor="name">
                Name:
              </Label>
              <Control>
                <Input
                  isSize="medium"
                  onChange={this.handleChange}
                  value={animal.name}
                  id="name"
                  type="text"
                  required={true}
                />
              </Control>
            </Field>
            <Field>
              <Label isSize="medium" htmlFor="arrivalDate">
                Arrival Date:
              </Label>
              <Control>
                <SingleDatePicker
                  id="arrivalDate"
                  onDateChange={this.handleDateChange}
                  date={moment(animal.arrivalDate)}
                  focused={focusState}
                  onFocusChange={this.handleDateFocusChange}
                  required={true}
                />
              </Control>
            </Field>
            <Field>
              {/* Label displays as fur color / fur pattern for cats, but we still store as breed */}
              <Label isSize="medium" htmlFor="breed">
                {animal.animalType === 'cat' ? 'Colour/Pattern:' : 'Breed:'}
              </Label>
              <Control>
                <Input onChange={this.handleChange} value={animal.breed} id="breed" type="text" isSize="medium" />
              </Control>
            </Field>
            <Field>
              <Label isSize="medium" htmlFor="active">
                Active:
              </Label>
              <Control>
                <Select
                  onChange={this.handleChange}
                  value={animal.active.toString()}
                  id="active"
                  type="text"
                  required={true}
                  isSize="medium"
                >
                  <option value=""> - </option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
              </Control>
            </Field>
            <Field>
              <Label isSize="medium" htmlFor="location">
                Location:
              </Label>
              <Control>
                <FlexSelect>
                  <Button
                    type="button"
                    isColor="primary"
                    aria-label="add location"
                    title="add location"
                    onClick={this.toggleModal}
                  >
                    <Icon className="fa fa-plus" />
                  </Button>
                  <Select
                    isSize="medium"
                    onChange={this.handleLocationChange}
                    value={animal.getLocationName()}
                    id="location"
                    type="text"
                    required={true}
                  >
                    <option value=""> - </option>
                    <option value="unknown">unknown</option>
                    {locations
                      .sort((a, b) => {
                        if (a.name.toLowerCase() < b.name.toLowerCase()) {
                          return -1;
                        }
                        if (a.name.toLowerCase() > b.name.toLowerCase()) {
                          return 1;
                        }
                        return 0;
                      })
                      .map((location, i) => (
                        <option key={`animal-locations-${location}-${i}`} value={location.name}>
                          {location.name}
                        </option>
                      ))}
                  </Select>
                </FlexSelect>
              </Control>
            </Field>
          </Box>

          <Box>
            <Title tag="h2" isSize={2}>
              Enrichment Info:
            </Title>
            <Field>
              <Label isSize="medium" htmlFor="trainingPlan">
                Training Plan:
              </Label>
              <Control>
                <Select
                  isSize="medium"
                  onChange={this.handleChange}
                  value={animal.trainingPlan}
                  id="trainingPlan"
                  type="text"
                  required={true}
                >
                  <option value=""> - </option>
                  <option value="no"> No </option>
                  <option value="yes"> Yes </option>
                </Select>
              </Control>
            </Field>
            <Field>
              <Label isSize="medium" htmlFor="assessmentStatus">
                Assessment Status:
              </Label>
              <Select
                isSize="medium"
                onChange={this.handleChange}
                value={animal.assessmentStatus}
                id="assessmentStatus"
                type="text"
                required={true}
              >
                <option value="initial">Initial Assessment Needed</option>
                <option value="full">Full Assessment Needed</option>
                <option value="complete">Assessment Complete</option>
              </Select>
            </Field>
            <Conditional check={animal.isCat()}>
              <Field>
                <Label isSize="medium" htmlFor="assessmentStatus">
                  Medical Tag:
                </Label>
                <Select
                  isSize="medium"
                  onChange={this.handleChange}
                  value={animal.medicalTag ? animal.medicalTag : 'null'}
                  id="medicalTag"
                >
                  <option value="null"> N/A </option>
                  <option value="red">Red</option>
                  <option value="green">Green</option>
                </Select>
              </Field>
            </Conditional>
            <Field>
              <Label isSize="medium" htmlFor="note">
                Note:
              </Label>
              <Control>
                <TextArea isSize="medium" onChange={this.handleChange} value={animal.note} id="note" />
              </Control>
            </Field>
          </Box>

          <Box>
            <Title tag="h2" isSize={2}>
              Other Info:
            </Title>
            <Field>
              <Label isSize="medium" htmlFor="colorCode">
                Color Code:
              </Label>
              <Select
                onChange={this.handleChange}
                value={animal.colorCode}
                id="colorCode"
                type="text"
                required={true}
                className="is-size-5"
              >
                <option value=""> - </option>
                <AnimalOptionsConsumer>
                  {({ animalOptions }) => {
                    if (!animalOptions) return null;

                    return Object.entries(animalOptions[animal.animalType].colorCodes).map(([key, val]) => (
                      <option key={`possibleColorCoption-${key}`} value={val}>
                        {val}
                      </option>
                    ));
                  }}
                </AnimalOptionsConsumer>
              </Select>
            </Field>
            <Conditional check={specialNeeds.length > 0}>
              <Field>
                <Title tag="h2" isSize={4}>
                  Special Needs:
                </Title>
                {specialNeeds
                  .filter(specialNeed => specialNeed.animalType === animal.animalType)
                  .map(specialNeed => (
                    <Field key={specialNeed._id}>
                      <Control>
                        <Checkbox
                          id={specialNeed._id}
                          checked={animal.specialNeeds.some(need => need._id === specialNeed._id)}
                          onChange={this.handleSpecialNeedChange.bind(this, specialNeed)}
                        >
                          <Spacer>{specialNeed.name}</Spacer>
                        </Checkbox>
                      </Control>
                    </Field>
                  ))}
              </Field>
            </Conditional>
            <Conditional check={animal.animalType === 'cat'}>
              <>
                <Field>
                  <Label isSize="medium" htmlFor="animalOrigin">
                    Animal Origin:
                  </Label>
                  <Select
                    isSize="medium"
                    value={animal.animalOrigin}
                    id="animalOrigin"
                    onChange={this.handleChange}
                    required={true}
                  >
                    <option value=""> - </option>
                    <option value="stray">Stray</option>
                    <option value="surrender">Owner Surrender</option>
                    <option value="hoarder">Hoarder</option>
                    <option value="transfer">Shelter (transfer)</option>
                    <option value="feral"> Feral </option>
                    <option value="born">Born in Shelter</option>
                    <option value="other">Other</option>
                  </Select>
                </Field>
                <Field>
                  <Label htmlFor="animalOrigin">Prefered:</Label>
                  <Select
                    isSize="medium"
                    value={animal.prefered.toString()}
                    id="prefered"
                    onChange={this.handleChange}
                    required={true}
                  >
                    <option value=""> - </option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Select>
                </Field>
              </>
            </Conditional>
          </Box>
          <Button type="submit" isColor="primary">
            Save and Exit
          </Button>
        </form>
        <AddLocationModal isOpen={isModalOpen} handleClose={this.toggleModal} refresh={this.getLocations} />
      </>
    );
  }
}

export default AnimalEditForm;
